import { EmberRosterRole, Entity, TileSize } from "game-common/models"
import { Callback, chunkSentence } from "game-common/util"
import { BitmapText, Graphics, LINE_JOIN, Text, TextMetrics } from "pixi.js"

import { EntityRenderMeta } from "../entity_rendering_manager"
import { Fader } from "../gui/fader"
import { BaseRenderable } from "./base_renderable"
import { BasicSprite } from "./basic_sprite"
import { TextRenderer } from "../text/text_renderer"
import { DyanmicTextContainer } from "../gui/dynamic_text_container"

// Chat speech-bubble geometry (see spokeImpl)
const BUBBLE_PAD_X = 12
const BUBBLE_PAD_Y = 10
const BUBBLE_RADIUS = 8
const BUBBLE_BORDER = 2
const BUBBLE_SHADOW_X = 3
const BUBBLE_SHADOW_Y = 4
const BUBBLE_FILL = 0x141821
const BUBBLE_FILL_ALPHA = 0.9
const BUBBLE_BORDER_COLOR = 0x707b91
const BUBBLE_BORDER_ALPHA = 0.75

// Compact segmented HP meter drawn beneath the name (variant B) -- replaces the old "|" pipe text.
// Colours mirror the roster HP bar (ember_roster drawHpBar) so the world and roster read the same.
const HP_BAR_W = 54
const HP_BAR_H = 7
const HP_SEGMENTS = 6
const HP_SEG_GAP = 2
const HP_CASE_PAD = 2
const HP_COLOR_FULL = 0x6fe06f
const HP_COLOR_WARN = 0xffd23f
const HP_COLOR_CRIT = 0xff5c5c

// Floating role glyph (👑 lord / 🛡️ defender / 🪓 forager) shown above an ember-camp NPC's head. It
// rests just above the head and lifts on hover to make room for the name + HP, then drops back down.
// The Y values are the tunable bits (like the HP-bar alignment was).
const GLYPH_SIZE = 15
const GLYPH_X = 18 // centred over the NPC (tunable)
const GLYPH_Y = 20 // rests just above the head; the glyph fades out on hover (it does not move)
const GLYPH_FADE_MS = 130
// Emoji hardcoded (not imported from gui/ember_roster) to avoid a sprites<->gui import cycle -- the
// same reason command_wheel keeps its own copy. Keep in sync with ROLE_EMOJI in ember_roster_card.ts.
const ROLE_GLYPH: Record<EmberRosterRole, string> = {
    lord: "👑",
    defender: "🛡️",
    forager: "🪓",
}

// Draw order of the per-entity widgets inside the HUD. These only take effect because the HUD turns
// on sortableChildren in the constructor -- without it pixi paints in addChild order, which put the
// role glyph and the sigil (added later, on demand) on top of the chat bubble.
const Z_TAG = 0
const Z_LABEL = 10 // name + HP meter
const Z_SIGIL = 20
const Z_ROLE_GLYPH = 30
const Z_POINTER = 40
const Z_CHAT_BUBBLE = 100000 // a bubble always reads over every other widget of its entity
// Every entity's HUD is a separate child of the world-overlay surface, so ordering the bubble within
// its own HUD isn't enough -- a neighbour's nameplate/glyph would still paint over it. The speaking
// entity's whole overlay item is lifted above the resting ones for as long as its bubble is up.
const Z_OVERLAY_RESTING = 0
const Z_OVERLAY_SPEAKING = 1000

// interface Job {
//     run: (done: Callback<void>) => void
// }

// class JobQueue {
//     private queue: Job[] = []
//     private running: boolean = false

//     constructor() {
//         //
//     }

//     add = (callback: Job) => {
//         if (this.queue.length > 2) {
//             this.queue.shift()
//         }
//         this.queue.push(callback)
//         if (!this.running) {
//             this.run()
//         }
//     }

//     private run = () => {
//         this.running = true
//         const callback = this.queue.shift()
//         if (callback) {
//             callback.run(() => this.run())
//         } else {
//             this.running = false
//         }
//     }
// }

export class AvatarHud extends BaseRenderable {
    id: string
    idText: Text
    hpBar: Graphics
    currentName: string = ""
    currentLevel: number
    currentColor: number
    currentHp: number = 0
    currentHpMax: number = 0

    roleGlyph?: Text
    currentRole: EmberRosterRole | null = null
    glyphAnimInterval: any

    alwaysShowLabel: boolean
    suppressDetailHover: boolean

    tag: Graphics

    idleTimeout: any
    blinkingInterval: any

    chatBubble: Graphics
    chatBubbleHorizontalSpacer: Graphics
    bubbleText: DyanmicTextContainer
    chatBubbleTimeout: any
    chatBubbleFadeInterval: any
    damageInterval: any

    pointerIcon: Graphics
    pointerIconPulseInterval: any
    pointerIconAngleInterval: any
    sigil: Graphics
    // chatQueue: JobQueue = new JobQueue()

    speaking: boolean

    constructor(
        color = 0xff00ff,
        renderMeta: EntityRenderMeta,
        parentArea: BaseRenderable,
        suppressDetailHover?: boolean,
    ) {
        super()
        this.parentArea = parentArea
        this.currentColor = color

        // Paint the HUD's widgets by zIndex (see the Z_* constants) rather than by addChild order.
        this.sortableChildren = true

        this.alwaysShowLabel = renderMeta.alwaysShowLabel

        this.idText = new TextRenderer().size("small").color(0xffffff).render() as Text
        // No backing plate (variant B): give the name a crisp black outline + soft shadow so it stays
        // legible over busy terrain.
        const nameStyle = this.idText.style
        nameStyle.stroke = "#000000"
        nameStyle.strokeThickness = 3
        nameStyle.lineJoin = "round"
        nameStyle.dropShadow = true
        nameStyle.dropShadowColor = "#000000"
        nameStyle.dropShadowAlpha = 0.55
        nameStyle.dropShadowBlur = 2
        nameStyle.dropShadowAngle = Math.PI / 2
        nameStyle.dropShadowDistance = 1

        // HP as a compact segmented meter drawn below the name (replaces the old "|" pipe text).
        this.hpBar = new Graphics()

        this.suppressDetailHover = suppressDetailHover
        const startVisible = !suppressDetailHover ? this.alwaysShowLabel : false
        this.idText.visible = startVisible
        this.hpBar.visible = startVisible
        this.idText.alpha = 1

        const tag = new Graphics()
        tag.beginFill(0xffffff)
        tag.lineStyle(1, 0xffffff)
        tag.drawCircle(-8, 8, 6)
        tag.endFill()
        tag.zIndex = Z_TAG
        this.addChild(tag)
        tag.visible = false
        tag.alpha = 0.7
        this.tag = tag

        this.idText.zIndex = Z_LABEL
        this.hpBar.zIndex = Z_LABEL
        this.addChild(this.idText)
        this.addChild(this.hpBar)

        this.update(3, 3)
        this.calcLabel()

        this.chatBubble = new Graphics()
        this.chatBubble.visible = true
        this.chatBubble.x = 0
        this.chatBubble.zIndex = Z_CHAT_BUBBLE
        this.addChild(this.chatBubble)

        const chatBubbleHorizontalSpacer = new Graphics()
        this.chatBubble.addChild(chatBubbleHorizontalSpacer)

        const bubbleText = this.makeBubbleText()
        this.chatBubble.addChild(bubbleText)
        this.chatBubble.visible = false

        const pointer = new BasicSprite({
            name: "icon-arrow.png",
            color: 0xffffff,
            scale: 1.0,
        })
        pointer.visible = false
        pointer.zIndex = Z_POINTER

        this.addChild(pointer)
        this.pointerIcon = pointer
    }

    makeBubbleText = () => {
        const bubbleText = new DyanmicTextContainer({
            // progressive is set per-utterance in spokeImpl (typewriter on the main view, instant in
            // the PiP focus), so this is just the default.
            progressive: true,
            // The styled panel drawn in spokeImpl is the bubble background now; the old inner
            // text-hugging backdrop just muddied it with a second dark rect, so it's disabled.
            backdrop: false,
        })
        bubbleText.x = BUBBLE_PAD_X
        bubbleText.y = BUBBLE_PAD_Y
        this.bubbleText = bubbleText

        return bubbleText
    }

    updateSigil(sigil?: string) {
        // remove existing sigil
        if (this.sigil) {
            this.sigil.parent?.removeChild(this.sigil)
        }

        if (sigil) {
            this.sigil = new BasicSprite({
                name: sigil,
                scale: 1.8,
                color: 0xffffff,
            })
            this.sigil.x += this.sigil.width * 0.3
            this.sigil.y = -3
            this.sigil.zIndex = Z_SIGIL
            this.addChild(this.sigil)
        }
    }

    // Show/replace the floating role glyph above the head (or remove it when the entity has no role).
    updateRole = (role: EmberRosterRole | null) => {
        if (this.currentRole === role) {
            return
        }
        this.currentRole = role

        if (this.glyphAnimInterval) {
            clearInterval(this.glyphAnimInterval)
            this.glyphAnimInterval = undefined
        }
        if (this.roleGlyph) {
            this.removeChild(this.roleGlyph)
            this.roleGlyph.destroy({ texture: true, baseTexture: true })
            this.roleGlyph = undefined
        }

        if (role) {
            // No .color() -> tint stays the no-op white so the emoji keeps its own colours.
            const glyph = new TextRenderer().fontSize(GLYPH_SIZE).render(ROLE_GLYPH[role]) as Text
            glyph.anchor.set(0.5)
            glyph.roundPixels = true
            glyph.x = GLYPH_X
            glyph.y = GLYPH_Y
            // Hidden while the label is showing (hovered / always-on), else visible above the head.
            const hidden = this.idText.visible || this.alwaysShowLabel
            glyph.alpha = hidden ? 0 : 1
            glyph.visible = !hidden
            glyph.zIndex = Z_ROLE_GLYPH
            this.addChild(glyph)
            this.roleGlyph = glyph
        }
    }

    // Fade the glyph in/out -- it hides on hover so the name/HP take its place, and fades back when
    // the pointer leaves. Runs a short self-clearing interval only while fading, so there's no
    // per-frame timer sitting idle.
    private fadeGlyphTo = (targetAlpha: number) => {
        const glyph = this.roleGlyph
        if (!glyph) {
            return
        }
        if (this.glyphAnimInterval) {
            clearInterval(this.glyphAnimInterval)
            this.glyphAnimInterval = undefined
        }
        if (targetAlpha > 0) {
            glyph.visible = true // fade-in from a hidden glyph must start visible
        }
        const startAlpha = glyph.alpha
        const delta = targetAlpha - startAlpha
        if (Math.abs(delta) < 0.02) {
            glyph.alpha = targetAlpha
            glyph.visible = targetAlpha > 0
            return
        }
        const stepMs = 16
        let elapsed = 0
        this.glyphAnimInterval = setInterval(() => {
            elapsed += stepMs
            const t = Math.min(1, elapsed / GLYPH_FADE_MS)
            glyph.alpha = startAlpha + delta * t
            if (t >= 1) {
                glyph.alpha = targetAlpha
                glyph.visible = targetAlpha > 0
                clearInterval(this.glyphAnimInterval)
                this.glyphAnimInterval = undefined
            }
        }, stepMs)
    }

    recolor = (color: number) => {
        if (this.currentColor === color) {
            return
        }
        this.currentColor = color
        if (this.tag) {
            this.tag.tint = color
        }
    }

    updateLevel = (level: number) => {
        if (this.currentLevel === level) {
            return
        }
        this.currentLevel = level
        this.calcLabel()
    }

    updateName = (name: string) => {
        if (name !== this.currentName) {
            this.currentName = name
            this.calcLabel()
        }
    }

    update = (hp: number, hpMax: number) => {
        if (this.currentHp !== hp) {
            this.currentHp = hp
            this.calcLabel()
        }
        if (this.currentHpMax !== hpMax) {
            this.currentHpMax = hpMax
            this.calcLabel()
        }
    }

    private calcLabel = () => {
        let name = this.currentName || ""
        const max = 15
        if (name.length > max) {
            name = name.substring(0, max - 3) + "..."
        }
        const level: string = !!name && this.currentLevel > 0 ? `Lv ${this.currentLevel}` : ""

        this.idText.y = 0
        this.idText.text = (name + "  " + level).trim()

        // Left-align the HP meter to the name's left edge, just below it.
        this.hpBar.x = this.idText.x
        this.hpBar.y = this.idText.y + this.idText.height + 2
        this.drawHpBar()
    }

    private drawHpBar = () => {
        const g = this.hpBar
        g.clear()
        if (this.currentHpMax < 1) {
            return
        }

        const ratio = Math.max(0, Math.min(1, this.currentHp / this.currentHpMax))
        const color = ratio < 0.4 ? HP_COLOR_CRIT : ratio < 0.7 ? HP_COLOR_WARN : HP_COLOR_FULL

        const innerW = HP_BAR_W - HP_CASE_PAD * 2
        const segW = (innerW - HP_SEG_GAP * (HP_SEGMENTS - 1)) / HP_SEGMENTS
        const segH = HP_BAR_H - HP_CASE_PAD * 2
        // Keep at least one segment lit while alive so a near-dead entity never reads as empty.
        const lit = this.currentHp > 0 ? Math.max(1, Math.round(ratio * HP_SEGMENTS)) : 0

        // Dark casing
        g.beginFill(0x000000, 0.72)
        g.drawRoundedRect(0, 0, HP_BAR_W, HP_BAR_H, 2)
        g.endFill()

        // Notched segments
        for (let i = 0; i < HP_SEGMENTS; i++) {
            const on = i < lit
            g.beginFill(on ? color : 0xffffff, on ? 1 : 0.1)
            g.drawRoundedRect(HP_CASE_PAD + i * (segW + HP_SEG_GAP), HP_CASE_PAD, segW, segH, 1)
            g.endFill()
        }
    }

    addTag() {
        this.tag.visible = true
        this.tag.tint = this.currentColor
    }

    removeTag() {
        this.tag.visible = false
    }

    // Raise/lower this entity's world-overlay item so a live bubble clears the HUDs of neighbouring
    // entities (their nameplates and role glyphs are siblings of this HUD, not children of it).
    private liftOverlay = (speaking: boolean) => {
        const overlayItem = this.parent
        if (!overlayItem) {
            return
        }
        overlayItem.zIndex = speaking ? Z_OVERLAY_SPEAKING : Z_OVERLAY_RESTING
    }

    spoke = (message: string, instant?: boolean) => {
        if (this.speaking) {
            this.bubbleText.wipe()
            this.bubbleText.parent.removeChild(this.bubbleText)

            this.bubbleText = this.makeBubbleText()
            this.chatBubble.addChild(this.bubbleText)
        }

        this.speaking = true
        this.spokeImpl(message, !!instant, () => {
            this.speaking = false
        })
    }

    spokeImpl = (message: string, instant: boolean, done: Callback<void>) => {
        if (this.chatBubbleTimeout) {
            clearTimeout(this.chatBubbleTimeout)
            this.chatBubbleTimeout = undefined
        }
        if (this.chatBubbleFadeInterval) {
            clearInterval(this.chatBubbleFadeInterval)
            this.chatBubbleFadeInterval = undefined
        }
        this.hpBar.visible = false
        this.idText.visible = false
        // A chat bubble takes over the label area; the glyph returns to its resting visible state.
        this.fadeGlyphTo(1)

        const wordCount = message.split(" ").length
        const delayMs = Math.max(3000, ((wordCount * 60) / 200) * 1250)

        const sentences = chunkSentence(message, 22)

        // Size the bubble to the ACTUAL rendered text rather than a per-char estimate -- the old
        // estimate under-sized the panel, so text crowded the right/top edges. Measure the wrapped
        // lines with the real bubble font and wrap the panel around them with uniform padding.
        const probe = new TextRenderer().variant("BubbleChatFont").render() as Text
        const metrics = TextMetrics.measureText(sentences.join("\n"), probe.style as any)
        probe.destroy({ texture: true, baseTexture: true })
        const bubbleW = Math.max(46, Math.ceil(metrics.width) + BUBBLE_PAD_X * 2)
        const bubbleH = Math.ceil(metrics.height) + BUBBLE_PAD_Y * 2

        this.bubbleText.x = BUBBLE_PAD_X
        this.bubbleText.y = BUBBLE_PAD_Y

        // Trace the whole bubble outline -- rounded rect plus a tail on the bottom edge -- as one
        // continuous path. Filling and stroking a single path keeps the border seamless around the
        // tail and the fill uniform (drawing the tail as a separate shape left a seam + colour gap).
        const r = BUBBLE_RADIUS
        const tailBaseLeftX = 14
        const tailBaseRightX = 30
        const tailTipX = 6
        const tailTipY = bubbleH + 14
        const tracePath = (ox: number, oy: number) => {
            const g = this.chatBubble
            g.moveTo(ox + r, oy)
            g.lineTo(ox + bubbleW - r, oy)
            g.quadraticCurveTo(ox + bubbleW, oy, ox + bubbleW, oy + r)
            g.lineTo(ox + bubbleW, oy + bubbleH - r)
            g.quadraticCurveTo(ox + bubbleW, oy + bubbleH, ox + bubbleW - r, oy + bubbleH)
            g.lineTo(ox + tailBaseRightX, oy + bubbleH)
            g.lineTo(ox + tailTipX, oy + tailTipY) // out to the tail tip, pointing down toward the entity
            g.lineTo(ox + tailBaseLeftX, oy + bubbleH)
            g.lineTo(ox + r, oy + bubbleH)
            g.quadraticCurveTo(ox, oy + bubbleH, ox, oy + bubbleH - r)
            g.lineTo(ox, oy + r)
            g.quadraticCurveTo(ox, oy, ox + r, oy)
            g.closePath()
        }

        this.chatBubble.clear()
        this.chatBubble.alpha = 1.0
        this.chatBubble.x = TileSize * 1.4
        this.chatBubble.zIndex = Z_CHAT_BUBBLE
        this.liftOverlay(true)

        // Soft drop shadow (same silhouette, offset, no stroke) for depth against the busy world
        this.chatBubble.lineStyle(0)
        this.chatBubble.beginFill(0x000000, 0.3)
        tracePath(BUBBLE_SHADOW_X, BUBBLE_SHADOW_Y)
        this.chatBubble.endFill()

        // Panel body + tail: one path, filled and bordered together for a seamless outline
        this.chatBubble.lineStyle({
            width: BUBBLE_BORDER,
            color: BUBBLE_BORDER_COLOR,
            alpha: BUBBLE_BORDER_ALPHA,
            join: LINE_JOIN.ROUND,
        })
        this.chatBubble.beginFill(BUBBLE_FILL, BUBBLE_FILL_ALPHA)
        tracePath(0, 0)
        this.chatBubble.endFill()

        this.chatBubble.visible = true

        this.bubbleText.progressive = !instant
        this.bubbleText.wipe()
        sentences.forEach(sentence => {
            this.bubbleText.append2(sentence, {
                font: "BubbleChatFont",
            })
        })

        const scheduleFade = () => {
            this.chatBubbleTimeout = setTimeout(() => {
                const fader: Fader = new Fader(this.chatBubble, {
                    releaseCallback: () => {
                        clearInterval(this.chatBubbleFadeInterval)
                        this.bubbleText.wipe()
                        this.chatBubble.visible = false
                        this.chatBubble.alpha = 1.0
                        this.liftOverlay(false)

                        done()
                    },
                })
                fader.update()
            }, delayMs * 0.5)
        }

        if (instant) {
            // PiP focus: the line is already fully drawn (non-progressive above), so just fade — no
            // typewriter, whose progressive-reveal objects flicker when rendered into the PiP texture.
            scheduleFade()
        } else {
            this.bubbleText.playback(scheduleFade)
        }
    }

    onMouseOver = (show: boolean) => {
        if (!this.suppressDetailHover && !this.speaking) {
            if (!this.alwaysShowLabel) {
                this.idText.visible = show
                this.hpBar.visible = show
                this.fadeGlyphTo(show ? 0 : 1)
            }

            if (show) {
                this.calcLabel()
            }
        }
    }

    updatePointerIcon(show: boolean, angle?: number) {
        if (!show) {
            clearInterval(this.pointerIconPulseInterval)
            clearInterval(this.pointerIconAngleInterval)
            this.pointerIconPulseInterval = undefined
            this.pointerIconAngleInterval = undefined
            this.pointerIcon.visible = false
            this.pointerIcon.angle = 0
        } else if (angle !== undefined) {
            this.pointerIcon.visible = true

            this.pointerIcon.angle = angle
            const radius = 32
            const x = radius * Math.sin((Math.PI * 2 * 90) / 360)
            const y = radius * Math.cos((Math.PI * 2 * 90) / 360)
            this.pointerIcon.x = x + radius - 10
            this.pointerIcon.y = y + radius + 10

            if (!this.pointerIconPulseInterval) {
                let scale = 1.0
                let scaleDir = 1

                this.pointerIconPulseInterval = setInterval(() => {
                    scale += 0.01 * scaleDir
                    if (scale > 1.2 || scale < 1.0) {
                        scaleDir = scaleDir * -1
                    }
                    this.pointerIcon.scale.set(scale)
                }, 50)
            }
        }
    }
}
